import { CartLinesUpdateEvent } from '@shopify/events';

class FurseraCartPerfectMatch extends HTMLElement {
  #products = [];
  #loadedProductId = '';
  #loadToken = 0;
  #trackObserver;
  #refreshTimer;
  #drawer;
  #needsRefresh = false;

  #handlePrevious = () => this.#scroll(-1);
  #handleNext = () => this.#scroll(1);
  #handleDrawerToggle = () => {
    if (!this.#drawer?.open) return;
    const force = this.#needsRefresh;
    this.#needsRefresh = false;
    this.#load(force);
  };

  static get observedAttributes() {
    return ['data-product-id'];
  }

  connectedCallback() {
    this.querySelector('[data-previous]')?.addEventListener('click', this.#handlePrevious);
    this.querySelector('[data-next]')?.addEventListener('click', this.#handleNext);
    this.#drawer = this.closest('dialog');
    this.#drawer?.addEventListener('toggle', this.#handleDrawerToggle);
    this.#observeTrack();
    if (this.#drawer?.open) this.#load();
  }

  disconnectedCallback() {
    this.querySelector('[data-previous]')?.removeEventListener('click', this.#handlePrevious);
    this.querySelector('[data-next]')?.removeEventListener('click', this.#handleNext);
    this.#drawer?.removeEventListener('toggle', this.#handleDrawerToggle);
    this.#drawer = undefined;
    this.#trackObserver?.disconnect();
    this.#trackObserver = undefined;
    window.clearTimeout(this.#refreshTimer);
    this.#refreshTimer = undefined;
    this.#loadToken += 1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;
    if (name === 'data-product-id') this.#scheduleRefresh();
  }

  #observeTrack() {
    if (this.#trackObserver) return;
    const track = this.querySelector('[data-track]');
    if (!track) return;

    this.#trackObserver = new MutationObserver(() => {
      // Section morphing restores the server-rendered empty track. Our own render
      // leaves cards in place, so it does not schedule another request.
      if (track.childElementCount === 0) this.#scheduleRefresh();
    });
    this.#trackObserver.observe(track, { childList: true });
  }

  #scheduleRefresh() {
    if (!this.#drawer?.open) {
      this.#needsRefresh = true;
      return;
    }

    window.clearTimeout(this.#refreshTimer);
    this.#refreshTimer = window.setTimeout(() => {
      this.#refreshTimer = undefined;
      if (this.isConnected) this.#load(true);
    }, 0);
  }

  async #load(force = false) {
    const productId = this.dataset.productId;
    if (!productId) return;

    if (!force && this.#loadedProductId === productId && this.#products.length) {
      this.hidden = false;
      return;
    }

    const loadToken = ++this.#loadToken;

    try {
      const [complementary, related] = await Promise.all([
        this.#fetch(productId, 'complementary'),
        this.#fetch(productId, 'related'),
      ]);
      if (loadToken !== this.#loadToken || this.dataset.productId !== productId) return;

      const cartProductIds = new Set(
        [...document.querySelectorAll('[data-product-id]')].map((element) => String(element.dataset.productId))
      );
      const seen = new Set();
      this.#products = [...complementary, ...related]
        .filter((product) => {
          const id = String(product?.id || '');
          if (!product?.available || !id || id === productId || cartProductIds.has(id) || seen.has(id)) {
            return false;
          }
          seen.add(id);
          return product.variants?.some((variant) => variant.available);
        })
        .slice(0, 8);

      this.#loadedProductId = productId;
      if (!this.#products.length) {
        this.hidden = true;
        return;
      }
      this.#render();
      this.hidden = false;
    } catch (error) {
      console.warn('[cart-perfect-match] Recommendations unavailable.', error);
    }
  }

  async #fetch(productId, intent) {
    const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '8');
    url.searchParams.set('intent', intent);
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    return (await response.json()).products || [];
  }

  #render() {
    const track = this.querySelector('[data-track]');
    if (!track) return;
    track.replaceChildren(...this.#products.map((product) => this.#card(product)));
  }

  #card(product) {
    const variant = product.variants.find((item) => item.available);
    const card = document.createElement('article');
    card.className = 'cart-perfect-match__card';

    const link = document.createElement('a');
    link.className = 'cart-perfect-match__image-link';
    link.href = product.url;
    const image = document.createElement('img');
    image.className = 'cart-perfect-match__image';
    image.loading = 'lazy';
    image.width = 240;
    image.height = 240;
    image.alt = product.title;
    image.src = this.#imageUrl(variant.featured_image || product.featured_image);
    link.append(image);

    const title = document.createElement('a');
    title.className = 'cart-perfect-match__name';
    title.href = product.url;
    title.textContent = product.title;

    const price = document.createElement('div');
    price.className = 'cart-perfect-match__price';
    const prefix = product.price_varies ? 'From ' : '';
    price.textContent = `${prefix}${this.#money(Number(variant.price))}`;

    const add = document.createElement('button');
    add.className = 'cart-perfect-match__add';
    add.type = 'button';
    add.setAttribute('aria-label', `Add ${product.title} to cart`);
    add.innerHTML = '<span aria-hidden="true">+</span>';
    add.addEventListener('click', () => this.#add(variant.id, add));

    card.append(link, title, price, add);
    return card;
  }

  #imageUrl(image) {
    const source = typeof image === 'string' ? image : image?.src || image?.url || '';
    if (!source) return '';
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', '320');
    return url.toString();
  }

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    }).format(cents / 100);
  }

  #scroll(direction) {
    const track = this.querySelector('[data-track]');
    const card = track?.querySelector('.cart-perfect-match__card');
    if (!track || !card) return;
    track.scrollBy({ left: direction * (card.getBoundingClientRect().width + 14), behavior: 'smooth' });
  }

  async #add(variantId, button) {
    if (!variantId || button.disabled) return;
    const status = this.querySelector('[data-status]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add product.');
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((cartResponse) => cartResponse.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-perfect-match',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      if (status) {
        status.textContent = 'Added to cart.';
        status.hidden = false;
      }
    } catch (error) {
      deferred.reject(error);
      if (status) {
        status.textContent = error.message || 'Unable to add product.';
        status.hidden = false;
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('fursera-cart-perfect-match')) {
  customElements.define('fursera-cart-perfect-match', FurseraCartPerfectMatch);
}
